import get from "lodash/get";
import startsWith from "lodash/startsWith";
import PropTypes from "prop-types";
import React, { useState } from "react";
import cx from "classnames";
import {
  calculateFreeTrialDaysLeft,
  getOrganizationCoach,
  isCoachFreeTrial,
  isPullActiveCoach,
  isTestAthlete,
} from "../utils/user-helpers";
import checkMenuExtended from "../utils/checkMenuExtended";
import getAvatarUrl from "../utils/get-avatar-url";
import { HeaderProfile } from "./header-elements";
import { AthleteLinks } from "./athlete-links";
import SvgIcon from "./SvgIcon";
import NotificationsDropdown from "./notificationsDropdown";
import { AthleteSelect } from "./inputs/athlete-select-new";
import submit from "submit-form";
import RecordButtonHeader from "./RecordButtonHeader";
import DownloadCoachMobileAppButtonHeader from "./DownloadCoachMobileAppButtonHeader";
import Drawer from "./drawer";
import { getRoleNameById } from "../views/dashboard/settings/manage-coaches/utils/manage-coaches-utils";

const coachingRoutes = [
  "/coaching-portal/coaching-dashboard",
  "/coaching-portal/athletes",
  "/coaching-portal/tasks",
];

function Header(
  {
    coachingPortalEnabled,
    athletePortalEnabled = false,
    mobile = false,
    handleAthleteSearch = () => {},
  },
  { locals }
) {
  const {
    activeUser,
    organizationOwner,
    activeUserToRequest,
    athlete = {},
    query,
    pathname,
    organizationRoles,
  } = locals;
  const { _id: userId, referralProgramInfo = {} } = activeUser;
  const { link: referralLink = "", showReferralButton = true } =
    referralProgramInfo;
  const isAdmin = activeUser.admin || activeUser.hasRole("admin");
  const isCoach = get(activeUser, "permissions", []).indexOf("coach") > -1;
  const isInvitedAthlete = get(activeUser, "coach", null);
  const isSubscriber =
    (!activeUser.organizationId && !organizationOwner) || !isCoach
      ? get(activeUser, "subscription", null)
      : get(organizationOwner, "subscription", null);
  const daysLeft = calculateFreeTrialDaysLeft(activeUser);

  const isCoachingPage = coachingRoutes.some((route) =>
    pathname.startsWith(route)
  );
  const isMenuExtended = checkMenuExtended(pathname);

  const showTestApp = React.useMemo(() => {
    const {
      athletes = [],
      pendingAthleteInvitations = [],
      email,
    } = activeUserToRequest;
    if (!athletes.length && !pendingAthleteInvitations.length) return false;
    const isAthleteList = pathname.startsWith("/coaching-portal/athletes");
    const testAthlete =
      athletes.find((athlete) => isTestAthlete(athlete.email, email)) ||
      pendingAthleteInvitations.find((athleteEmail) =>
        isTestAthlete(athleteEmail, email)
      );
    return isAthleteList && !testAthlete;
  }, [activeUser, pathname]);

  const showPushTestApp = React.useMemo(() => {
    const isStore = pathname === "/coaching-portal/store";
    const isPushInvited = locals.userByEmail && locals.userByEmail !== null;
    const appStoreListingEnable = locals.activeUser.appStoreListingEnable;

    return isStore && appStoreListingEnable && !isPushInvited;
  }, [activeUser, pathname]);

  const isUserInCoachingPortal = Boolean(pathname?.includes("coaching-portal"));
  const userHasCustomReferralLink = referralLink.length > 0;
  const isActivePull = isPullActiveCoach(activeUser);
  const isCoachTrial = isCoachFreeTrial(activeUser);
  const roleName = !!activeUser.organizationRoleId
    ? getRoleNameById(organizationRoles, activeUser.organizationRoleId)
    : false;
  const isOwner = roleName === "Owner";
  /**
   * Function to close forever the container who shows the referral link information
   * @param {*} e
   */
  const handleNotShowReferralContainer = (e) => {
    e.preventDefault();
    submit("/actions/user/update-referral-program", {
      method: "POST",
      body: {
        purpose: "hide-link",
        userId,
        referralProgramInfo: {
          link: referralLink,
          showReferralButton: false,
        },
      },
    });
  };
  const showActivityFeed = () => {
    let body = { "modal[type]": "activity-feed" };
    if (isOwner) body = { ...body, isOwner: true };
    submit("/actions/refresh", {
      method: "GET",
      body,
    });
  };
  return (
    <header role="main">
      {!isMenuExtended && !athletePortalEnabled && (
        <div>
          <img alt="msb-logo" src="/img/svg/MSB-ico.svg" />
        </div>
      )}
      {isMenuExtended && !athletePortalEnabled && isCoachingPage ? (
        mobile ? ( //not-reviewed
          <div role="search-icon-wrapper" onClick={handleAthleteSearch}>
            <img className="icon" src="/img/svg/search-ico.svg" alt="search" />
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <AthleteSelect
              locals={locals}
              refreshWithName={true}
              handleAthleteChange={(athleteId) => {
                const { usersOrganization } = locals;
                const { organizationActiveUsers } = usersOrganization || [];

                const { isFromOtherOrganizationMember, organizationCoachId } =
                  getOrganizationCoach(
                    locals.activeUser,
                    athleteId,
                    organizationActiveUsers
                  );
                let body = { athlete: athleteId };
                if (isFromOtherOrganizationMember)
                  body = { ...body, organizationCoach: organizationCoachId };

                submit(`/athlete-portal/profile`, {
                  method: "GET",
                  body,
                });
              }}
            />
            {isCoach && (
              <DownloadCoachMobileAppButtonHeader
                activeUser={activeUser}
                isActivePull={isActivePull}
              />
            )}
          </div>
        )
      ) : athletePortalEnabled ? (
        athletePortalEnabled && (
          <div className="flex flex-row gap-2 items-center">
            <div className="athlete-select dropdown-athlete">
              <div
                className="avatar"
                style={{
                  backgroundImage: `url(${getAvatarUrl(locals.athlete)})`,
                }}
              />
              <AthleteSelect
                locals={locals}
                {...(pathname === "/athlete-portal/metrics/annual-planner" && {
                  athleteSwitch: true,
                })}
              />
            </div>

            <RecordButtonHeader
              locals={locals}
              BUTTON_ID={"loom-record-sdk-button-header"}
            />
          </div>
        )
      ) : (
        <div className="flex items-center gap-4">
          {pathname.startsWith("/coaching-portal/community-chat") ? (
            <h2>Community Chat</h2>
          ) : pathname.startsWith("/coaching-portal/chat") ? (
            <h2>Chats</h2>
          ) : (
            <></>
          )}
          {isCoach && (
            <DownloadCoachMobileAppButtonHeader
              activeUser={activeUser}
              isActivePull={isActivePull}
            />
          )}
        </div>
      )}

      <hgroup
        style={{
          zIndex: 10,
        }}
      >
        {isCoach && showTestApp && (
          <a
            role="button"
            data-button-type="outline"
            href="/coaching-portal/athletes/?modal[type]=test-account"
          >
            <span>Test Your App</span>
          </a>
        )}
        {isCoach &&
          activeUserToRequest.coachingFreeTrial &&
          !athletePortalEnabled &&
          daysLeft > 0 && (
            <div className="free-trial-header-div">
              <a
                href="/coach-billing"
                role="button"
                data-button-type="secondary"
              >
                Free trial: {daysLeft} days left.
                <span className="clipped font-semibold">&nbsp;Upgrade Now</span>
              </a>
            </div>
          )}
        {isUserInCoachingPortal &&
          !userHasCustomReferralLink &&
          isActivePull &&
          !isCoachTrial &&
          showReferralButton && (
            <div className="free-trial-header-div mobile-hidden">
              <a
                href="https://mystrengthbook.firstpromoter.com/"
                role="button"
                data-button-type="secondary"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="referral-div">
                  <span className="referral-text">Earn cash with our</span>
                  <span className="clipped font-semibold h-5">
                    &nbsp;Referral Program
                  </span>
                </div>
                <button
                  className="referral-icon"
                  onClick={(e) => {
                    handleNotShowReferralContainer(e);
                  }}
                >
                  <img
                    src="/img/new-icons/cross-light.svg"
                    className="filter-grey"
                  />
                </button>
              </a>
            </div>
          )}
        {athletePortalEnabled && (
          <AthleteLinks athlete={athlete} pathName={pathname} query={query} />
        )}
        {isCoach && !isMenuExtended && !athletePortalEnabled && (
          <a href="/coaching-portal" role="button" data-button-type="outline">
            <span>Coaching</span>
          </a>
        )}
        <div className="nav-icons">
          {!isCoach && (
            <a
              href="/dashboard/support/contact"
              role="button"
              data-button-type="outline"
            >
              <SvgIcon src="/img/svg/need-help-icon.svg" />
            </a>
          )}

          {/* Test your app */}

          {isCoach && showPushTestApp && (
            <a
              href={`/actions/refresh?modal[type]=test-account`}
              role="button"
              data-button-type="outline"
            >
              Test My PUSH App
            </a>
          )}

          {!isSubscriber && <NotificationsDropdown mobile={mobile} />}
          {!isSubscriber && mobile && (
            <div className="notifications-black-background" />
          )}

          <a
            href={
              coachingPortalEnabled || athletePortalEnabled
                ? "/coaching-portal/settings"
                : "/dashboard/settings"
            }
            role="button"
            data-button-type="secondary"
            data-icon
            className={`icon-element mobile-hidden ${cx({
              active: startsWith(
                pathname,
                coachingPortalEnabled
                  ? "/coaching-portal/settings"
                  : "/dashboard/settings"
              ),
            })}`}
          >
            <SvgIcon src="/img/new-icons/settings.svg" alt="" />
          </a>
          <a
            href={
              coachingPortalEnabled || athletePortalEnabled
                ? "/coaching-portal/settings/settings-menu"
                : "/dashboard/settings"
            }
            className={`icon-element only-show-mobile ${cx({
              active: startsWith(
                pathname,
                coachingPortalEnabled
                  ? "/coaching-portal/settings"
                  : "/dashboard/settings"
              ),
            })}`}
          >
            <SvgIcon src="/img/new-icons/settings.svg" alt="" />
          </a>
          {isCoach && (
            <button
              data-button-type="secondary"
              onClick={showActivityFeed}
              data-icon
              data-icon-only
            >
              <SvgIcon
                src="/img/new-icons/vuesax-linear-activity.svg"
                classNames="cursor-pointer"
              />
            </button>
          )}
        </div>
        <HeaderProfile
          activeUser={activeUser}
          isAdmin={isAdmin}
          isInvitedAthlete={isInvitedAthlete}
          isSubscriber={isSubscriber}
          locals={locals}
          activeUserToRequest={activeUserToRequest}
          daysLeft={daysLeft}
        />
      </hgroup>
    </header>
  );
}

Header.contextTypes = { locals: PropTypes.object };

export default Header;
